/**
 * Decides what a visitor sees: a loading placeholder, the login form, the forced password change,
 * or the application.
 *
 * Both applications wrap their content in this, so the four states are resolved once and identically.
 * The `expired` case renders the application *with* the re-authentication dialog over it, because the
 * employee's work is still on screen and still theirs — see `SessionExpiredDialog`.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ChangePasswordGate } from "./ChangePasswordGate";
import { LoginPage } from "./LoginPage";
import { SessionExpiredDialog } from "./SessionExpiredDialog";
import { useSession } from "./SessionProvider";

export interface AuthGateProps {
  title: string;
  children: ReactNode;
}

export function AuthGate({ title, children }: AuthGateProps): JSX.Element {
  const { t } = useTranslation("auth");
  const { status, mustChangePassword } = useSession();

  if (status === "loading") {
    // Announced, because on a slow line this is the whole page for a second or two and a screen
    // reader would otherwise be told nothing at all.
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-surface"
        role="status"
        aria-live="polite"
      >
        <span className="text-body-md text-textMuted">{t("session.checking")}</span>
      </div>
    );
  }

  if (status === "anonymous") return <LoginPage title={title} />;

  if (mustChangePassword) return <ChangePasswordGate title={title} />;

  return (
    <>
      {children}
      <SessionExpiredDialog />
    </>
  );
}
